/** @module Auth0BetaOptInHelper */
/* eslint-disable import/extensions */
import Auth0Constants from '../auth0-constants.mjs';
import Auth0UtilsBrowser from '../auth0-utils-browser.mjs';
import Auth0BetaOptInFeatureMachine from './auth0-beta-opt-in-feature-machine.mjs';
import Auth0BetaOptInFeatureFlag from './auth0-beta-opt-in-feature-flag.mjs';
import Auth0BetaOptInLocalUser from './auth0-beta-opt-in-feature-local-user.mjs';
import {
  activeBetaFeatures,
  OPT_IN_TYPE_MACHINE_LEVEL,
  OPT_IN_TYPE_FEATURE_FLAG,
  OPT_IN_TYPE_USER_LEVEL,
  // import any active beta IDs here...
  // example:
  // BETA_FEATURE_MY_NEW_FEATURE_AVAILABLE,
  // BETA_FEATURE_MY_NEW_FEATURE_ENABLED,
} from './auth0-active-beta-features-config.mjs';
import logger from '../logging/auth0-logger.mjs';

class Auth0BetaOptInHelper {
  constructor(options = {}) {
    console.log(`Auth0BetaOptInHelper init => ${JSON.stringify(options)}`);
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options.tenantOrDomain);
    this._auth0Tenant = resolved.tenant.id;
    this._ldClientSideId = options.ldClientSideId;
    this._allBetaFeatures = activeBetaFeatures
      .map((abf) => {
        try {
          return this._optInObjectFactory(abf);
        } catch (error) {
          logger.warn(`Feature flag ${abf.featureId} failed to initialize.`, error);
          return null;
        }
      })
      .filter(abf => abf !== null);
  }

  _optInObjectFactory = (optInDescriptor) => {
    switch (optInDescriptor.type) {
      case OPT_IN_TYPE_FEATURE_FLAG:
        return new Auth0BetaOptInFeatureFlag({
          featureId: optInDescriptor.featureId,
          auth0Tenant: this._auth0Tenant,
          ldClientSideId: this._ldClientSideId,
          // deriveOwnContextData: !this._readOnlyMode,
        });
      case OPT_IN_TYPE_MACHINE_LEVEL:
        return new Auth0BetaOptInFeatureMachine({
          featureId: optInDescriptor.featureId,
        });
      case OPT_IN_TYPE_USER_LEVEL:
        return new Auth0BetaOptInLocalUser({
          featureId: optInDescriptor.featureId,
        });
      default:
        throw new Error(`Unable to produce opt in object for descriptor => ${JSON.stringify(optInDescriptor)}`);
    }
  };

  optIn = (featureKey) => {
    console.log(`Auth0BetaOptInHelper.optIn for "${featureKey}"`);
    this._findOptInObject(featureKey)?.optIn();
  };

  optOut = (featureKey) => {
    console.log(`Auth0BetaOptInHelper.optOut for "${featureKey}"`);
    this._findOptInObject(featureKey)?.optOut();
  };

  clear = () => {};

  isOptedIn = async (featureKey) => {
    const optInObj = this._findOptInObject(featureKey);
    const isOptedIn = await optInObj.isOptedIn();
    return isOptedIn;
  };

  _findOptInObject = (featureKey) => {
    const found = this.betaFeatures.find(abf => abf.featureId === featureKey);
    if (!found) {
      throw new Error(`Unable to locate an opt in feature with ID "${featureKey}"`);
    }
    return found;
  };

  get betaFeatures() {
    return this._allBetaFeatures;
  }

  get betaFeatureIds() {
    return activeBetaFeatures.map(abf => abf.featureId);
  }

  static get _persistedBetaOptInData() {
    const optInData = Auth0UtilsBrowser.getOption({ keyName: Auth0Constants.AUTH0_BETA_OPT_IN_KEY }, false, true);
    return !optInData ? {} : optInData;
  }

  // Specific feature sets here...
  // Continued example from auth0-active-beta-features-config.mjs...

  // examples:
  // get BETA_FEATURE_MY_NEW_FEATURE_AVAILABLE_ID() {
  //   return BETA_FEATURE_MY_NEW_FEATURE_AVAILABLE;
  // }

  // get BETA_FEATURE_MY_NEW_FEATURE_ENABLED_ID() {
  //   return BETA_FEATURE_MY_NEW_FEATURE_ENABLED;
  // }
}

export default Auth0BetaOptInHelper;
